import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '../api/client';
import { AuthContext } from '../App';
import Sidebar from '../components/Sidebar';
import './ListStyles.css';
import './QuotationStyles.css';

const ProductList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get(`/categories`);
      const categoryData = res.data?.data || res.data;
      setCategories(Array.isArray(categoryData) ? categoryData : []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [token]);

  const fetchProducts = useCallback(async (reset = false) => {
    if (loading) return;

    try {
      setLoading(true);
      const currentPage = reset ? 1 : page;

      const params = {
        page: currentPage,
        limit,
        ...(selectedCategory ? { categoryId: selectedCategory } : {}),
      };
      const res = search.trim()
        ? await api.get(`/products/search`, { params: { q: search.trim(), limit, ...(selectedCategory ? { categoryId: selectedCategory } : {}) } })
        : await api.get(`/products`, { params });

      if (res.data?.success) {
        const newProducts = res.data.data || [];
        if (reset) {
          setProducts(newProducts);
        } else {
          setProducts(prev => {
            const map = new Map();
            [...prev, ...newProducts].forEach(p => map.set(p.id, p));
            return Array.from(map.values());
          });
        }

        if (res.data.meta) {
          setHasMore(currentPage < res.data.meta.totalPages);
        } else {
          setHasMore(newProducts.length === limit);
        }
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, page, search, selectedCategory, token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // FIXED: Remove fetchProducts from dependency array to prevent infinite loop
  // The dependencies should only be the values that trigger a search, not the function itself
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchProducts(true);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedCategory, token]);

  // FIXED: Separate effect for page changes
  useEffect(() => {
    if (page > 1) {
      fetchProducts(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const renderImage = (product) => {
    const image = product.images?.[0]?.image;
    if (!image) return null;
    return (
      <img
        src={/^https?:\/\//i.test(image) ? image : `${API_BASE}/uploads/products/${image}`}
        alt={product.name || 'Product'}
        className="list-thumb"
      />
    );
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="page-header-quotation">
          <div className="container">
            <h1 className="page-title">Product List</h1>
            <p className="page-subtitle">Manage products and open edit pages</p>
          </div>
        </div>

        <div className="container">
          <div className="list-toolbar">
            <div className="list-helper">
              {products.length} product{products.length === 1 ? '' : 's'}
            </div>
            <div className="list-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/products/browse')}
              >
                Browse for Quotation
              </button>
              <Link to="/products/new" className="btn btn-primary">
                Add Product
              </Link>
            </div>
          </div>

          <div className="list-filters">
            <input
              type="text"
              placeholder="Search by name, code, or keywords"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="list-search-input"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="list-select"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {(search || selectedCategory) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setSelectedCategory('');
                }}
                className="btn btn-secondary btn-sm"
              >
                Clear Filters
              </button>
            )}
          </div>

          {loading && products.length === 0 ? (
            <div className="loading-container">
              <div className="spinner large"></div>
              <p>Loading products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">No Data</div>
              <div className="empty-text">No products found</div>
              <div className="empty-subtext">Try adjusting your search or filters</div>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Code</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id}>
                      <td>
                        <div className="list-item-cell">
                          {renderImage(product)}
                          <div>
                            <div className="list-item-name">{product.name}</div>
                            <div className="list-item-subtext">ID: {product.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>{product.product_code || '-'}</td>
                      <td>{product.category?.name || '-'}</td>
                      <td>INR {Number(product.price || 0).toFixed(2)}</td>
                      <td>
                        <span className={`list-status ${product.status || 'active'}`}>
                          {product.status || 'active'}
                        </span>
                      </td>
                      <td>
                        <div className="list-row-actions">
                          <Link to={`/products/${product.id}/edit`} className="btn btn-secondary btn-sm">
                            Edit
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hasMore && products.length > 0 && (
            <div className="list-footer">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductList;
